import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useMemo } from "react";
import { ArrowRightSLine } from "../../IconV2/react";
import Link from "../../Link";
import Text from "../../Text";
import { colors, spacings } from "../../Theme";
import { CHECKBOX_CN, CLOSE_ON_CLICK_CN, KEYBOARD_ACTIVABLE_CN, REMOVED, SELECTED_CN } from "..";

const findSlot = (children: React.ReactNode, name: string): React.ReactNode =>
  (Array.isArray(children) ? children : [children]).filter(c => c && c.props.slot === name);

const useSlot = (
  children: React.ReactNode,
  name: string,
  props?: Record<string, unknown>,
): React.ReactNode =>
  useMemo(
    () =>
      props
        ? React.Children.map(findSlot(children, name), s =>
            React.isValidElement(s) ? React.cloneElement(s, props) : s,
          )
        : findSlot(children, name),
    [children, name, props],
  );

export const FragmentSlot = memo(
  ({ children }: React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>) => <>{children}</>,
);

type Props = {
  disabled?: boolean;
  selected?: boolean;
  removed?: boolean;
  closeOnClick?: boolean;
  withRightArrow?: boolean;
  withTextEllipsis?: boolean;
  to?: string;
  href?: React.AnchorHTMLAttributes<HTMLAnchorElement>["href"];
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  titleText?: string;
  noLabelElement?: boolean;
} & React.HTMLAttributes<HTMLElement>;

function OptionV2_(
  {
    disabled = false,
    selected = false,
    removed = false,
    closeOnClick = false,
    withRightArrow = false,
    withTextEllipsis = false,
    children,
    href,
    to,
    className,
    title,
    noLabelElement = false,
    ...props
  }: React.PropsWithChildren<Props>,
  ref: React.Ref<HTMLAnchorElement> | React.Ref<HTMLDivElement> | React.Ref<HTMLLabelElement>,
) {
  const text = useSlot(children, "text");
  const description = useSlot(children, "description");
  const extra = useSlot(children, "extra");
  const icon = useSlot(children, "icon", { color: colors.carbon800 });
  const checkbox = useSlot(children, "checkbox", { disabled });

  const content = useMemo(
    () => (
      <>
        {icon}
        {checkbox}
        <Text
          type="body2"
          title={typeof title === "string" ? title : ""}
          className={cx(optionText, { disabled, ellipsis: withTextEllipsis })}
        >
          {text}
        </Text>
        <div
          className={cx(optionExtra, {
            disabled,
            description: Array.isArray(description) && description.length > 0,
          })}
        >
          {extra}
        </div>
        {Array.isArray(description) && description.length > 0 && (
          <Text type="body3" className={cx(optionDescription, { disabled })}>
            {description}
          </Text>
        )}
        {withRightArrow && <ArrowRightSLine className={cx(optionArrow, { disabled })} />}
      </>
    ),
    [checkbox, description, disabled, extra, icon, text, title, withRightArrow, withTextEllipsis],
  );

  const classNames = useMemo(
    () =>
      cx(optionContainer, className, {
        [optionDisabled]: disabled,
        [optionSelected]:
          selected && (!checkbox || (Array.isArray(checkbox) && checkbox.length === 0)),
        [CHECKBOX_CN]: checkbox,
        [KEYBOARD_ACTIVABLE_CN]: !disabled,
        [SELECTED_CN]: selected,
        [CLOSE_ON_CLICK_CN]: closeOnClick,
        [REMOVED]: removed,
      }),
    [checkbox, className, closeOnClick, disabled, removed, selected],
  );

  if ([href, to].some(str => typeof str === "string" && str.length > 0)) {
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classNames}
        href={href}
        to={to}
        {...props}
      >
        {content}
      </Link>
    );
  }

  if (!noLabelElement && Array.isArray(checkbox) && checkbox.length > 0) {
    return (
      <label ref={ref as React.Ref<HTMLLabelElement>} className={classNames} {...props}>
        {content}
      </label>
    );
  }

  return (
    <div ref={ref as React.Ref<HTMLDivElement>} className={classNames} {...props}>
      {content}
    </div>
  );
}

export const OptionV2 = React.memo(React.forwardRef<HTMLAnchorElement, Props>(OptionV2_));

const optionText = css`
  align-self: center;
  grid-column: 2 / span 1;
  grid-row: 1 / span 1;
  width: 100%;
  overflow: hidden;

  &.disabled {
    opacity: 0.5;
  }

  &.ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const optionDescription = css`
  grid-column: 2 / span 1;
  grid-row: 2 / span 1;
  color: ${colors.carbon600};

  &.disabled {
    opacity: 0.5;
  }
`;

const optionContainer = css`
  cursor: pointer;
  align-items: center;
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-auto-columns: min-content;
  column-gap: ${spacings.xs};
  padding: ${spacings.xs};
  border-radius: 4px;

  &:has(:first-child:is(.${optionText})) {
    grid-template-columns: 1fr;
  }

  > :first-child:is(.${optionText}),
  > :first-child:is(.${optionText}),
  > :first-child:is(.${optionText}) ~ .${optionDescription} {
    grid-column: 1 / span 2;
  }

  &:hover {
    background-color: ${colors.titanium100};
  }
`;

const optionSelected = css`
  background-color: ${colors.lightNitrogen50};
`;

const optionArrow = css`
  grid-column: 4 / span 1;
  grid-row: 1 / span 1;

  &.disabled {
    opacity: 0.5;
  }
`;

const optionExtra = css`
  align-self: start;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  grid-column: 3 / span 1;
  grid-row: 1 / span 1;
  max-width: 100px;

  &.description {
    grid-row: 1 / span 2;
  }

  &:empty {
    display: none;
  }

  &.disabled {
    opacity: 0.5;
  }
`;

const optionDisabled = css`
  cursor: not-allowed;

  svg {
    opacity: 0.5;
  }

  &:hover {
    background-color: initial;
  }
`;
